import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.scss';
import { DateCarousel } from 'widget/event_components/Ticket/components/Datetime/components/DateCarousel';
import Divider from '@mui/material/Divider';
import { ColorDescription } from 'widget/event_components/Ticket/components/Datetime/ColorDescription';
import moment from 'moment';
import Periods from 'widget/event_components/Ticket/components/Datetime/components/Periods';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { List } from 'immutable';

class DateTimeEntriesFactory {
  constructor(occurrences) {
    this.occurrences = occurrences;
  }

  sortTimes(timeEntries) {
    return timeEntries.sort((a, b) => {
      const firstIterateDate = a.get('start_date');
      const secondIterateDate = b.get('start_date');

      if (moment(firstIterateDate).isAfter(secondIterateDate)) return 1;
      if (moment(firstIterateDate).isBefore(secondIterateDate)) return -1;

      return 0;
    });
  }

  timeEntriesIterator(timeEntries) {
    return this.sortTimes(timeEntries);
  }

  occurrencesIterator() {
    return this.sortTimes(this.occurrences).map((occurrence) => ({
      start: moment(occurrence.get('start_date')).tz(occurrence.get('timezone')),
      end: moment(occurrence.get('end_date')).tz(occurrence.get('timezone')),
      id: occurrence.get('id'),
      timeEntries: this.timeEntriesIterator(occurrence.get('time_entries')),
    }));
  }

  getEntries() {
    return this.occurrencesIterator();
  }
}

const BackToDatePlate = ({ occurrenceDate }) => (
  <div className={styles.backToDateContainer}>
    <div className={styles.dateInPlate}>
      <CalendarMonthIcon />
      {occurrenceDate.format('ddd, D MMMM YYYY')}
    </div>
  </div>
);

const MultiSessionSelector = ({ event, settings, onSelectAccessType, selectedAccessType }) => {
  const isMultiSession = event.get('multi_session_event');

  if (!isMultiSession) return null;

  return (
    <div className={styles.multiSessionWrapper}>
      <div className={styles.multiSessionPeriod}>
        <div className={styles.multiSessionTitle}>{settings.get('multi_session_label_text') || 'Event Access'}</div>
        <div className={styles.multiSessionLine}>
          <div
            className={`${styles.multiSessionItem} ${selectedAccessType === 'single' ? styles.multiSessionSelected : ''}`}
            onClick={() => onSelectAccessType('single')}
          >
            {settings.get('single_session_selection_text') || 'Single Session'}
          </div>
          <div
            className={`${styles.multiSessionItem} ${selectedAccessType === 'all' ? styles.multiSessionSelected : ''}`}
            onClick={() => onSelectAccessType('all')}
          >
            {settings.get('multi_session_selection_text') || 'All Sessions'}
          </div>
        </div>
      </div>
      {settings.get('multi_session_additional_text') && (
        <div className="multi-selector-additional-text">{settings.get('multi_session_additional_text')}</div>
      )}
      <Divider className={styles.divider} variant="fullWidth" />
    </div>
  );
};

export const CommonView = ({
  event,
  occurrences = List(),
  setTickets,
  setEvent,
  settings,
  occurrenceDate,
  onClickBack,
  setOccurrencesDate,
  onResizeComponent,
}) => {
  const [selectedOccurrence, setSelectedOccurrence] = useState();
  const [selectedAccessType, setSelectedAccessType] = useState('single');
  const autoSelectedIdRef = useRef(null);

  const entries = useMemo(
    () => new DateTimeEntriesFactory(occurrences.filter((o) => !o.get('is_series_parent'))).getEntries(),
    [occurrences, occurrenceDate]
  );

  useEffect(() => {
    if (occurrenceDate) {
      occurrences.forEach((occurrence) => {
        const choiceDate = moment(occurrenceDate.tz(occurrence.get('timezone')));
        const eventDate = moment(moment(occurrence.get('start_date')).tz(occurrence.get('timezone')));

        if (choiceDate.isSame(eventDate, 'day')) {
          setSelectedOccurrence(occurrence.get('id'));
        }
      });
    } else if (occurrences.first && occurrences.first()) {
      setSelectedOccurrence(occurrences.first().get('id'));
    }
  }, [occurrences, occurrenceDate]);

  useEffect(() => {
    if (selectedAccessType === 'all') {
      // Collect all tickets from all occurrences and time entries
      const parentOccurrence = occurrences.find((e) => e.get('multi_session_event') && e.get('is_series_parent')).set('multi_session_access', true);
      const allTickets = parentOccurrence.get('tickets');
      setTickets(allTickets);
      autoSelectedIdRef.current = 'all';
    } else if (selectedAccessType === 'single' && settings.get('autoSelectSingleDay') && event.get('multi_session_event') && entries?.size === 1) {
      // Auto-select when there's only one occurrence/date entry (for multi-session events only)
      const singleEntry = entries.get(0);
      if (singleEntry) {
        if (singleEntry.timeEntries && singleEntry.timeEntries.size === 1) {
          // Single time entry - select it automatically
          const singleTimeEntry = singleEntry.timeEntries.first();
          const timeEntryId = singleTimeEntry.get('id');

          // Only set if we haven't already auto-selected this time entry
          if (autoSelectedIdRef.current !== timeEntryId) {
            setEvent(singleTimeEntry);
            setTickets(singleTimeEntry.get('tickets'));
            autoSelectedIdRef.current = timeEntryId;
          }
        } else if (singleEntry.timeEntries && singleEntry.timeEntries.size === 0) {
          // No time entries - select the occurrence itself
          const occurrence = occurrences.find((o) => o.get('id') === singleEntry.id);
          if (occurrence) {
            const occurrenceId = occurrence.get('id');

            // Only set if we haven't already auto-selected this occurrence
            if (autoSelectedIdRef.current !== occurrenceId) {
              setEvent(occurrence);
              setTickets(occurrence.get('tickets') || List());
              autoSelectedIdRef.current = occurrenceId;
            }
          }
        }
      }
    } else if (selectedAccessType === 'single' && settings.get('autoSelectSingleTime') && selectedOccurrence) {
      // Auto-select time when the selected occurrence has only 1 time entry
      const selectedEntry = entries.find((record) => record.id === selectedOccurrence);
      if (selectedEntry && selectedEntry.timeEntries && selectedEntry.timeEntries.size === 1) {
        const singleTimeEntry = selectedEntry.timeEntries.first();
        const timeEntryId = singleTimeEntry.get('id');

        if (autoSelectedIdRef.current !== timeEntryId) {
          setEvent(singleTimeEntry);
          setTickets(singleTimeEntry.get('tickets'));
          autoSelectedIdRef.current = timeEntryId;
        }
      } else if (selectedEntry && (!selectedEntry.timeEntries || selectedEntry.timeEntries.size === 0)) {
        // No time entries - select the occurrence itself
        const occurrence = occurrences.find((o) => o.get('id') === selectedEntry.id);
        if (occurrence) {
          const occurrenceId = occurrence.get('id');

          if (autoSelectedIdRef.current !== occurrenceId) {
            setEvent(occurrence);
            setTickets(occurrence.get('tickets') || List());
            autoSelectedIdRef.current = occurrenceId;
          }
        }
      }
    }
  }, [selectedAccessType, occurrences, setTickets, entries, settings, setEvent, selectedOccurrence, event]);

  const handleDateOnClick = useCallback((id) => {
    onResizeComponent && onResizeComponent();
    setSelectedOccurrence(id);
  }, []);

  const handleAccessTypeSelect = useCallback(
    (accessType) => {
      setSelectedAccessType(accessType);
      onResizeComponent && onResizeComponent();

      if (accessType === 'all') {
        // Set the event with multi_session_access flag
        setEvent(occurrences.find((e) => e.get('multi_session_event') && e.get('is_series_parent')).set('multi_session_access', true));
      }
    },
    [event, setEvent]
  );

  const currentEntries = useMemo(() => entries.find((record) => record.id === selectedOccurrence), [selectedOccurrence, entries]);

  // Determine if we should auto-select and hide the time periods selector
  const shouldAutoSelect =
    settings.get('autoSelectSingleTime') &&
    selectedAccessType === 'single' &&
    currentEntries &&
    currentEntries.timeEntries &&
    currentEntries.timeEntries.size === 1;

  return (
    <div className={styles.wrapper}>
      <MultiSessionSelector event={event} settings={settings} onSelectAccessType={handleAccessTypeSelect} selectedAccessType={selectedAccessType} />
      {selectedAccessType === 'single' && (
        <>
          {occurrenceDate ? (
            <BackToDatePlate occurrenceDate={occurrenceDate} onClickBack={onClickBack} />
          ) : (
            <DateCarousel dateTimeEntries={entries} selectedOccurrence={selectedOccurrence} onClickOccurrence={handleDateOnClick} />
          )}
          <Divider className={styles.divider} variant="fullWidth" />
          {!shouldAutoSelect && (
            <Periods
              setOccurrencesDate={setOccurrencesDate}
              event={event}
              setEvent={setEvent}
              setTickets={setTickets}
              selectedOccurrence={currentEntries}
              settings={settings}
              key={event.get('id')}
            />
          )}
        </>
      )}
      <ColorDescription settings={settings} className={styles.colorDescriptionWrapper} />
    </div>
  );
};
